import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { FieldArray, type FormikProps } from 'formik';
import { useMemo } from 'react';

import './VendorLeadStepContactTab.scss';

import { SelectField, TextInputField } from 'src/components/base';
import type { IVendorProductLeadContactTabFormValues } from 'src/components/vendors/steps/Contact/VendorLeadStepContactTab.validation';
import { VendorLeadStepLayoutNextButton } from 'src/components/vendors/steps/VendorLeadStepLayoutNextButton';
import { castToOptionWithEmpty } from 'src/helpers/castToOption';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import { CONTACT_TYPE_LABEL, ContactType } from 'src/types/ContactType';
import type { IContactWithReferenceCount } from 'src/types/IContactWithReferenceCount';

const contactTypeOptions = castToOptionWithEmpty({
  enumeration: ContactType,
  formatter: CONTACT_TYPE_LABEL,
});

type VendorLeadStepContactTabContentProps = {
  formikProps: FormikProps<IVendorProductLeadContactTabFormValues>;

  onUpdate: (newValues: IVendorProductLeadContactTabFormValues) => void;
};

export function VendorLeadStepContactTabContent({
  formikProps: { values, validateForm },
  onUpdate,
}: Readonly<VendorLeadStepContactTabContentProps>) {
  const canAddNewContact = useMemo(() => values.contacts.every((contact) => !!contact.id), [values.contacts]);

  useDebounceEffect(
    () => {
      void (async () => {
        const errors = await validateForm(values);

        if (Object.keys(errors).length === 0) {
          onUpdate(values);
        }
      })();
    },
    [values.contacts],
    1000
  );

  return (
    <div className="vendor-lead-step-contact-tab__container">
      <FieldArray
        name="contacts"
        render={(arrayHelpers) => (
          <>
            {values.contacts.map((contact, i) => {
              const isContactEditingDisabled = contact.referenceCount > 1;

              return (
                <div key={contact.id} className="vendor-lead-step-contact-tab__container__contact">
                  {i > 0 && <div className="vendor-lead-step-contact-tab__container__delimiter" />}
                  <div className="vendor-lead-step-contact-tab__container__header">
                    <h2>
                      Contact {i + 1} {contact.id ? '' : '(New)'}
                    </h2>
                    {(canAddNewContact ? values.contacts.length > 1 : values.contacts.length > 2 || !contact.id) &&
                      !isContactEditingDisabled && (
                        <Button
                          className="margin-left-auto"
                          type="default"
                          icon={<CloseOutlined />}
                          onClick={arrayHelpers.handleRemove(i)}
                        />
                      )}
                  </div>

                  <div className="vendor-lead-step-contact-tab__container__row">
                    <div className="vendor-lead-step-contact-tab__container__row">
                      <div className="w-100">
                        <TextInputField
                          name={`contacts[${i}].firstName`}
                          label="First Name"
                          disabled={isContactEditingDisabled}
                        />
                      </div>
                      <div className="w-100">
                        <TextInputField
                          name={`contacts[${i}].lastName`}
                          label="Last Name"
                          disabled={isContactEditingDisabled}
                        />
                      </div>
                    </div>
                    <div className="vendor-lead-step-contact-tab__container__row">
                      <div className="w-100">
                        <TextInputField
                          name={`contacts[${i}].emailAddress`}
                          label="Email"
                          disabled={isContactEditingDisabled}
                        />
                      </div>
                      <div className="w-50">
                        <SelectField
                          name={`contacts[${i}].contactType`}
                          label="Type"
                          options={contactTypeOptions}
                          disabled={isContactEditingDisabled}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="vendor-lead-step-contact-tab__container__row">
                    <div className="w-100">
                      <TextInputField
                        name={`contacts[${i}].cellPhone`}
                        label="Cell phone"
                        type="phone"
                        disabled={isContactEditingDisabled}
                      />
                    </div>
                    <div className="vendor-lead-step-contact-tab__container__subrow">
                      <div className="w-100">
                        <TextInputField
                          name={`contacts[${i}].plainPhone`}
                          label="Phone"
                          type="phone"
                          disabled={isContactEditingDisabled}
                        />
                      </div>
                      <TextInputField
                        className="w-50"
                        name={`contacts[${i}].plainPhoneExt`}
                        type="digit"
                        label="Ext."
                        disabled={isContactEditingDisabled}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            {canAddNewContact && (
              <Button
                className="vendor-lead-step-contact-tab__container__add-button"
                type="primary"
                ghost={true}
                size="middle"
                icon={<PlusOutlined />}
                onClick={arrayHelpers.handlePush({
                  id: '',
                  contactType: null,
                  firstName: '',
                  lastName: '',
                  emailAddress: '',
                  plainPhone: null,
                  plainPhoneExt: null,
                  cellPhone: null,
                  referenceCount: 0,
                } as IContactWithReferenceCount)}
              >
                Add contact
              </Button>
            )}
          </>
        )}
      />
      <div className="vendor-lead-step__layout__center__footer-actions">
        <VendorLeadStepLayoutNextButton />
      </div>
    </div>
  );
}
